import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { StatusPill, type PillTone } from '@siliconx/shared/ui/StatusPill';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import {
  BENCH_ATTACH_EFFECTS,
  BENCH_ATTACH_LANDS_WITHIN_SECONDS,
  BENCH_ATTACH_REFUSAL_MESSAGES,
  benchAttachWarnings,
  type BenchAttachCandidate,
  type BenchAttachWarning,
  type BenchBoardSerialMatch,
  type BenchSerialCompare,
} from '@siliconx/shared';
import { attachBenchPairing, cancelBenchAttach, getBenchRun } from '../../lib/bench';
import { AgentText } from './AgentText';

/**
 * SIL-1708 (E-22). Continue a RUNNING run on a freshly booted machine instead of starting a new one.
 *
 * Every serial, drive key and subject shown here is an AGENT CLAIM (pair/start is unauthenticated). So the page never asks the
 * human to compare two strings by eye: the comparisons are computed in code (shared helpers, server side) and shown as badges,
 * every claim is escaped with hidden characters made visible, and the human must always confirm that the code came off the
 * screen of the machine in front of them (a phished code is the classic device-code attack). The server still decides every
 * refusal; this panel is UX only and renders only the fixed shared messages, never server text.
 */

export const PROVENANCE_LABEL = 'I read this code from the screen of the machine in front of me and its label matches';
export const WARNING_ACK_LABEL = 'I understand the warnings above and still want to continue this run';

/** Plain words for each reason the second confirmation is required. */
export function attachWarningCopy(w: BenchAttachWarning, c: BenchAttachCandidate): string {
  switch (w) {
    case 'board_serial_not_same':
      return c.boardSerialMatch === 'different'
        ? 'The board serial this machine reports is DIFFERENT from the board serial recorded for the run.'
        : 'The board serial cannot be compared: it is missing on the machine or on the run.';
    case 'other_serial_differs':
      return 'The product or chassis serial this machine reports is different from, or missing against, the run record. Only one serial has to match for a run to be offered here.';
    case 'self_reported':
      return 'This run is self-reported. Its network address is not checked against a registered bench site, so your confirmation is the only control.';
    case 'outside_run_site':
      return 'The machine is outside the network of the bench site this run started on.';
    case 'old_code':
      return 'The code has been showing for more than 5 minutes. It may have been passed on to someone who is not at the machine.';
  }
}

const COMPARE_BADGE: Record<BenchSerialCompare, { tone: PillTone; label: string; title: string }> = {
  same: { tone: 'green', label: 'same', title: 'Equal to the value recorded for the run, checked by the server.' },
  different: { tone: 'red', label: 'DIFFERENT', title: 'Not equal to the value recorded for the run, or it contains hidden or non-ASCII characters.' },
  missing: { tone: 'amber', label: 'missing', title: 'Only one side has a usable value, so the two cannot be compared.' },
  absent: { tone: 'neutral', label: 'absent', title: 'Neither the run nor the machine has a value.' },
};

function CompareBadge({ value }: { value: BenchSerialCompare | BenchBoardSerialMatch }) {
  const b = COMPARE_BADGE[value];
  return (
    <StatusPill tone={b.tone} title={b.title}>
      {b.label}
    </StatusPill>
  );
}

const ENVIRONMENT_LABEL = { linux: 'Linux', winpe: 'WinPE' } as const;

function errorCodeOf(err: unknown): string | undefined {
  return (err as { body?: { error?: { code?: unknown } } } | null)?.body?.error?.code as string | undefined;
}
function errorStatusOf(err: unknown): number | undefined {
  return (err as { status?: number } | null)?.status;
}

/**
 * Fixed copy per code. The server message is never shown. A code the UI does not know falls back to a generic line.
 * `not_found` is the counted code miss (the pairing is gone or the code is wrong), `too_many_attempts` the limiter.
 */
export function attachErrorMessage(err: unknown): string {
  const code = errorCodeOf(err);
  if (code && Object.prototype.hasOwnProperty.call(BENCH_ATTACH_REFUSAL_MESSAGES, code)) {
    return BENCH_ATTACH_REFUSAL_MESSAGES[code as keyof typeof BENCH_ATTACH_REFUSAL_MESSAGES];
  }
  if (code === 'too_many_attempts' || errorStatusOf(err) === 429) return 'Too many attempts. Wait 10 minutes, then try again.';
  if (code === 'not_found') return 'This code is not valid or has expired. Check the code on the bench screen.';
  if (errorStatusOf(err) === 503) return BENCH_ATTACH_REFUSAL_MESSAGES.try_again;
  return 'Something went wrong. Try again.';
}

/**
 * The answer may not have reached us (lost 200, timeout) or the retry may meet an already-spent code (counted 404). Only these
 * are worth a look at the run before reporting a failure; a definitive refusal (a known code) is final.
 */
function isAmbiguous(err: unknown): boolean {
  const code = errorCodeOf(err);
  if (code === 'not_found') return true;
  const status = errorStatusOf(err);
  return status === undefined || status >= 500;
}

/** Slack for clock skew between this browser and the server when comparing `approvedAt` with the click time. */
const APPROVAL_CLOCK_SKEW_MS = 60_000;
/** How often the age of the code is re-evaluated on a page left open. */
const AGE_TICK_MS = 15_000;

/**
 * SF1. The approval counts as OURS only when the run's pending attach was recorded by this user at or after the click that
 * started this attempt. A pending attach from another user, or an older approval of our own, says nothing about this machine.
 */
async function approvalAlreadyRecorded(runId: string, since: number): Promise<boolean> {
  try {
    const pending = (await getBenchRun(runId)).pendingAttach;
    if (!pending || pending.byYou !== true) return false;
    const at = Date.parse(pending.approvedAt);
    return Number.isFinite(at) && at >= since - APPROVAL_CLOCK_SKEW_MS;
  } catch {
    return false;
  }
}

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  return `${m} min ${seconds % 60} s`;
}

function formatStarted(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function SerialRow({
  label,
  presented,
  compare,
  matched,
  runValue,
  showRunValue,
}: {
  label: string;
  presented: string | null;
  compare: BenchSerialCompare | BenchBoardSerialMatch;
  matched: boolean;
  runValue?: string | null;
  showRunValue: boolean;
}) {
  return (
    <tr className="border-t border-border align-top">
      <th scope="row" className="py-1 pr-3 text-left font-normal text-muted-foreground">
        {label}
      </th>
      <td className="py-1 pr-3">
        <AgentText value={presented} />
        {matched && (
          <StatusPill tone="neutral" className="ml-2" title="This value equals the identity recorded for the run.">
            matched the run
          </StatusPill>
        )}
      </td>
      <td className="py-1 pr-3">{showRunValue ? <AgentText value={runValue ?? null} /> : <span className="text-muted-foreground">-</span>}</td>
      <td className="py-1">
        <CompareBadge value={compare} />
      </td>
    </tr>
  );
}

function DriveSection({ c }: { c: BenchAttachCandidate }) {
  if (!c.nvmeCompared) {
    return (
      <p data-testid="nvme-section">
        <span className="font-medium">Drives: not compared.</span>{' '}
        <span className="text-muted-foreground">
          The run recorded no NVMe drives, so a missing or extra drive cannot be detected.
        </span>
      </p>
    );
  }
  const same = c.nvmeKeysMissing.length === 0 && c.nvmeKeysNew.length === 0 && !c.nvmeKeysTruncated;
  return (
    <div data-testid="nvme-section" className="space-y-1">
      <p>
        <span className="font-medium">Drives:</span>{' '}
        {same
          ? `the same ${c.nvmeRunKeyCount} NVMe ${c.nvmeRunKeyCount === 1 ? 'drive' : 'drives'} as the run.`
          : `the run saw ${c.nvmeRunKeyCount}, this boot reports ${c.nvmePresentedKeyCount}.`}
      </p>
      {c.nvmeKeysMissing.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground">In the run, not reported by this boot</p>
          <ul className="space-y-0.5">
            {c.nvmeKeysMissing.map((k, i) => (
              <li key={`m${i}`}>
                <AgentText value={k} max={96} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {c.nvmeKeysNew.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground">Reported by this boot, never seen in the run</p>
          <ul className="space-y-0.5">
            {c.nvmeKeysNew.map((k, i) => (
              <li key={`n${i}`}>
                <AgentText value={k} max={96} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {c.nvmeKeysTruncated && <p className="text-xs text-status-amber">The drive lists were cut at a limit. More differences may exist.</p>}
    </div>
  );
}

type CardState = 'idle' | 'approved' | 'cancelled';

function AttachCandidateCard({
  code,
  c,
  environment,
  deviceCount,
  locked,
  lookedUpAt,
  onPending,
  onAttached,
}: {
  code: string;
  c: BenchAttachCandidate;
  environment: 'linux' | 'winpe' | undefined;
  deviceCount: number | null;
  /** Another candidate is being attached, or an attach was already approved for this code. */
  locked: boolean;
  /** When the lookup that produced `c` was fetched (ms epoch). `pairingAgeSeconds` is the age at that moment. */
  lookedUpAt: number | undefined;
  onPending: (runId: string | null) => void;
  onAttached: (runId: string) => void;
}) {
  const [provenance, setProvenance] = useState(false);
  // N1: the second confirmation belongs to the warning set it was given for; a changed set needs a fresh tick.
  const [ackedKey, setAckedKey] = useState<string | null>(null);
  const [state, setState] = useState<CardState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // Set after a failure that may have recorded the approval anyway: the next try looks at the run before posting again.
  const mayHaveLanded = useRef(false);
  // SF1: click time of the first try of this attempt (kept across retries after an ambiguous failure).
  const attemptSince = useRef(0);
  // N2: React Query reports isPending a render late, so two quick clicks could both post.
  const inFlight = useRef(false);

  // SF3: the lookup is cached for good, so the code keeps ageing while the page is open. Add the time since the fetch.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), AGE_TICK_MS);
    return () => clearInterval(t);
  }, []);
  const withAge = (at: number): BenchAttachCandidate =>
    lookedUpAt === undefined ? c : { ...c, pairingAgeSeconds: c.pairingAgeSeconds + Math.max(0, Math.floor((at - lookedUpAt) / 1000)) };
  const shown = withAge(now);

  const warnings = benchAttachWarnings(shown);
  const warnKey = warnings.join(',');
  if (ackedKey !== null && ackedKey !== warnKey) setAckedKey(null);
  const warningsAcked = ackedKey !== null && ackedKey === warnKey;
  const warnId = `attach-warn-${c.runId}`;
  const provId = `attach-prov-${c.runId}`;
  const isRegistered = c.attestationLevel === 'registered_bench';

  const attach = useMutation({
    mutationFn: async () => {
      // N4: after a lost 200 or a failed try, the approval may already exist. A pendingAttach on the run is success.
      if (mayHaveLanded.current && (await approvalAlreadyRecorded(c.runId, attemptSince.current))) return;
      try {
        await attachBenchPairing(code, c.runId);
      } catch (err) {
        if (isAmbiguous(err)) {
          mayHaveLanded.current = true;
          if (await approvalAlreadyRecorded(c.runId, attemptSince.current)) return;
        }
        throw err;
      }
    },
    onMutate: () => {
      if (!mayHaveLanded.current) attemptSince.current = Date.now();
      setError(null);
      onPending(c.runId);
    },
    onSuccess: () => {
      mayHaveLanded.current = false;
      setState('approved');
      onAttached(c.runId);
    },
    onError: (err) => setError(attachErrorMessage(err)),
    onSettled: () => {
      inFlight.current = false;
      onPending(null);
    },
  });

  function onContinue() {
    if (inFlight.current) return;
    // SF3: re-check the age at the moment of the click. If a warning appeared since the last tick, show it and ask again.
    const at = Date.now();
    if (benchAttachWarnings(withAge(at)).join(',') !== warnKey) {
      setNow(at);
      return;
    }
    inFlight.current = true;
    attach.mutate();
  }

  const cancel = useMutation({
    mutationFn: () => cancelBenchAttach(c.runId),
    onMutate: () => setCancelError(null),
    onSuccess: () => {
      setState('cancelled');
    },
    onError: (err) => {
      const code2 = errorCodeOf(err);
      if (code2 === 'none_open') setCancelError('Nothing to cancel: the machine already continued the run, or the approval expired.');
      else if (code2 === 'not_found') setCancelError(BENCH_ATTACH_REFUSAL_MESSAGES.run_not_found);
      else if (errorStatusOf(err) === 503) setCancelError(BENCH_ATTACH_REFUSAL_MESSAGES.try_again);
      else setCancelError('The approval could not be cancelled. Try again.');
    },
  });

  const canSubmit = provenance && (warnings.length === 0 || warningsAcked) && !attach.isPending && !locked;

  return (
    <Card className="space-y-3" data-testid={`attach-candidate-${c.runId}`}>
      <PanelTitle
        title={`Running since ${formatStarted(c.startedAt)}`}
        description={`Started by ${c.approvedByEmail}`}
        right={
          <StatusPill tone={isRegistered ? 'green' : 'amber'}>{isRegistered ? 'Registered bench' : 'Self-reported'}</StatusPill>
        }
      />

      <p className="text-sm">
        <span className="text-muted-foreground">Boot environment:</span>{' '}
        <span className="font-medium">{environment ? ENVIRONMENT_LABEL[environment] : 'Not reported'}</span>
        <span className="text-muted-foreground"> - devices found: </span>
        <span className="font-medium">{deviceCount ?? 'Not reported'}</span>
      </p>

      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th className="py-1 pr-3 font-normal">Serial</th>
            <th className="py-1 pr-3 font-normal">Reported by the machine</th>
            <th className="py-1 pr-3 font-normal">Run record</th>
            <th className="py-1 font-normal">Check</th>
          </tr>
        </thead>
        <tbody>
          <SerialRow
            label="Product"
            presented={c.presented.productSerial}
            compare={c.productSerialCompare}
            matched={c.matchedFields.includes('productSerial')}
            showRunValue={false}
          />
          <SerialRow
            label="Chassis"
            presented={c.presented.chassisSerial}
            compare={c.chassisSerialCompare}
            matched={c.matchedFields.includes('chassisSerial')}
            showRunValue={false}
          />
          <SerialRow
            label="Board"
            presented={c.presented.boardSerial}
            compare={c.boardSerialMatch}
            matched={c.matchedFields.includes('boardSerial')}
            runValue={c.runBoardSerial}
            showRunValue
          />
        </tbody>
      </table>

      <div className="space-y-1 text-sm">
        <p>
          <span className="text-muted-foreground">Code shown from IP </span>
          <code className="font-mono">{c.pairingSourceIp}</code>
          <span className="text-muted-foreground"> {formatAge(shown.pairingAgeSeconds)} ago. </span>
          {isRegistered ? (
            c.inRunSite ? (
              <span className="text-status-green">Inside the bench site this run started on.</span>
            ) : (
              <span className="text-status-amber">Outside the bench site this run started on.</span>
            )
          ) : (
            <span className="text-muted-foreground">Not checked: a self-reported run has no bench site.</span>
          )}
        </p>
        <DriveSection c={c} />
        {(c.crosscheckSubjects.length > 0 || c.crosscheckTruncated) && (
          <div data-testid="crosscheck-section">
            <p className="text-xs text-muted-foreground">Hardware that conflicts with the run&apos;s first scan</p>
            <ul className="space-y-0.5">
              {c.crosscheckSubjects.map((s, i) => (
                <li key={i}>
                  <AgentText value={s} max={96} />
                </li>
              ))}
            </ul>
            {c.crosscheckTruncated && <p className="text-xs text-status-amber">This list was cut at a limit.</p>}
          </div>
        )}
      </div>

      <div className="space-y-1 text-sm">
        <p className="font-medium">What happens when the machine continues this run</p>
        <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground" data-testid="attach-effects">
          {BENCH_ATTACH_EFFECTS.map((e) => (
            <li key={e.id}>{e.text}</li>
          ))}
        </ul>
      </div>

      {warnings.length > 0 && (
        <InsightBanner tone="amber" lead="Check before you continue">
          <ul id={warnId} className="list-disc space-y-1 pl-5" data-testid="attach-warnings">
            {warnings.map((w) => (
              <li key={w}>{attachWarningCopy(w, shown)}</li>
            ))}
          </ul>
        </InsightBanner>
      )}

      {state === 'idle' && (
        <div className="space-y-2">
          <label htmlFor={provId} className="flex items-start gap-2 text-sm">
            <input
              id={provId}
              type="checkbox"
              checked={provenance}
              disabled={attach.isPending || locked}
              onChange={(e) => setProvenance(e.target.checked)}
              className="mt-1"
            />
            <span>{PROVENANCE_LABEL}</span>
          </label>
          {warnings.length > 0 && (
            <label htmlFor={`${warnId}-ack`} className="flex items-start gap-2 text-sm">
              <input
                id={`${warnId}-ack`}
                type="checkbox"
                checked={warningsAcked}
                disabled={attach.isPending || locked}
                onChange={(e) => setAckedKey(e.target.checked ? warnKey : null)}
                className="mt-1"
              />
              <span>{WARNING_ACK_LABEL}</span>
            </label>
          )}
          {error && (
            <p role="alert" className="text-sm text-status-red">
              {error}
            </p>
          )}
          <Button onClick={onContinue} disabled={!canSubmit}>
            Continue this run
          </Button>
        </div>
      )}

      {state === 'approved' && (
        <div className="space-y-2" data-testid="attach-approved">
          <InsightBanner tone="neutral" lead="Approved">
            The machine continues the run on its next check-in, within {Math.round(BENCH_ATTACH_LANDS_WITHIN_SECONDS / 60)} minutes. Until
            then you can cancel.
          </InsightBanner>
          {cancelError && (
            <p role="alert" className="text-sm text-status-red">
              {cancelError}
            </p>
          )}
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
              Cancel approval
            </Button>
            <Link to="/bench/runs/$runId" params={{ runId: c.runId }} className="text-sm underline">
              Open the run
            </Link>
          </div>
        </div>
      )}

      {state === 'cancelled' && (
        <InsightBanner tone="neutral" lead="Approval cancelled">
          The machine will not continue this run. This code is used up: pair the machine again for a new code.
        </InsightBanner>
      )}
    </Card>
  );
}

export function AttachRunPanel({
  code,
  candidates,
  environment,
  deviceCount,
  lookedUpAt,
  onBusyChange,
  onAttached,
}: {
  code: string;
  candidates: BenchAttachCandidate[];
  environment?: 'linux' | 'winpe';
  deviceCount: number | null;
  /** When the lookup behind `candidates` was fetched (ms epoch); the age of the code is counted on from it. */
  lookedUpAt?: number;
  /** True while an attach request is in flight (the page disables its other actions meanwhile). */
  onBusyChange?: (busy: boolean) => void;
  /** Called once an approval is recorded (the page then hides "Start a new run": the code is used up). */
  onAttached?: (runId: string) => void;
}) {
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);
  const [attachedRunId, setAttachedRunId] = useState<string | null>(null);

  return (
    <section className="space-y-3" aria-label="Continue a running run">
      <PanelTitle
        title="Continue a running run"
        description="This machine matches a run that is still running. Continue it instead of starting a new one."
      />
      {candidates.length > 1 && (
        <InsightBanner tone="amber" lead="More than one running run matches this machine">
          Two runs share this machine identity, so the server may refuse to attach until one of them is finished or abandoned. Check the
          start times and pick the run you mean.
        </InsightBanner>
      )}
      {candidates.map((c) => (
        <AttachCandidateCard
          key={c.runId}
          code={code}
          c={c}
          environment={environment}
          deviceCount={deviceCount}
          locked={(pendingRunId !== null && pendingRunId !== c.runId) || (attachedRunId !== null && attachedRunId !== c.runId)}
          lookedUpAt={lookedUpAt}
          onPending={(id) => {
            setPendingRunId(id);
            onBusyChange?.(id !== null);
          }}
          onAttached={(id) => {
            setAttachedRunId(id);
            onAttached?.(id);
          }}
        />
      ))}
    </section>
  );
}
